<template>
  <Modal :open="modelValue" :close-on-esc="true" :show-close-button="false" @close="close">
    <Combobox @update:modelValue="onSelect">
      <div class="-mx-4 -mt-5 sm:-mx-8 sm:-mt-8 flex items-center border-b border-gray-100 px-4">
        <SearchIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
        <ComboboxInput
          ref="inputRef"
          class="h-12 w-full border-0 bg-transparent pl-3 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:outline-hidden focus:ring-0"
          placeholder="Rechercher une organisation, un store, un contrat, un sinistre…"
          autocomplete="off"
          spellcheck="false"
          :value="keywords"
          @input="onInput"
          @keydown.esc="close"
        />
        <button
          type="button"
          aria-label="Fermer"
          class="shrink-0 p-1 text-gray-400 hover:text-gray-600"
          @mousedown.prevent
          @click.stop="close"
        >
          <XIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div class="-mx-4 sm:-mx-8 max-h-96 overflow-y-auto">
        <p v-if="!hasMinKeywords" class="px-4 py-6 text-center text-sm text-gray-500" data-test="initial-state">
          Tapez pour rechercher…
        </p>

        <p v-else-if="loading" class="px-4 py-6 text-center text-sm text-gray-500" data-test="loading-state">
          Recherche en cours…
        </p>

        <ComboboxOptions v-else-if="hasResults" static as="div" class="py-2">
          <section
            v-for="(section, sectionIndex) in visibleSections"
            :key="section.key"
            :class="['px-2', sectionIndex === 0 ? 'pb-2' : 'border-t border-gray-100 pt-2 pb-2']"
          >
            <h3 class="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
              {{ section.label }}
            </h3>
            <ComboboxOption
              v-for="item in section.items"
              :key="item.optionKey"
              :value="item"
              v-slot="{ active }"
              as="template"
            >
              <div
                :class="[
                  'flex cursor-pointer items-center justify-between rounded-md px-2 py-2',
                  active ? 'bg-primary text-white' : 'text-gray-900',
                ]"
                :data-test="`result-${section.key}`"
              >
                <div class="flex flex-col truncate">
                  <span class="truncate text-sm">{{ item.primary }}</span>
                  <span :class="['truncate text-xs', active ? '' : 'text-gray-500']">
                    {{ item.secondary }}
                  </span>
                </div>
              </div>
            </ComboboxOption>
          </section>
        </ComboboxOptions>

        <p v-else class="px-4 py-6 text-center text-sm text-gray-500" data-test="empty-state">Aucun résultat</p>
      </div>

      <div
        class="-mx-4 -mb-4 sm:-mx-8 sm:-mb-8 flex items-center gap-4 border-t border-gray-100 bg-gray-50 px-4 py-2 rounded-b-lg"
      >
        <span class="flex items-center gap-1 text-xs text-gray-400">
          <kbd
            class="inline-flex items-center rounded-sm border border-gray-300 bg-white px-1.5 py-0.5 font-sans text-xs text-gray-500"
          >
            &#8593;&#8595;
          </kbd>
          Naviguer
        </span>
        <span class="flex items-center gap-1 text-xs text-gray-400">
          <kbd
            class="inline-flex items-center rounded-sm border border-gray-300 bg-white px-1.5 py-0.5 font-sans text-xs text-gray-500"
          >
            &#8629;
          </kbd>
          Sélectionner
        </span>
        <span class="flex items-center gap-1 text-xs text-gray-400">
          <kbd
            class="inline-flex items-center rounded-sm border border-gray-300 bg-white px-1.5 py-0.5 font-sans text-xs text-gray-500"
          >
            Esc
          </kbd>
          Fermer
        </span>
      </div>
    </Combobox>
  </Modal>
</template>

<script>
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/vue";
import { SearchIcon, XIcon } from "@heroicons/vue/outline";
import { Modal, toCamelCase } from "@estaly/ui";
import QuickSearchService from "@/api/services/quickSearch";

const DEBOUNCE_MS = 250;

const emptyResults = () => ({
  organizations: [],
  stores: [],
  billingEntities: [],
  planSales: [],
  claims: [],
});

const joinSecondary = (parts) => parts.filter(Boolean).join(" · ");

export default {
  name: "QuickSearchDialog",
  components: {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    Modal,
    SearchIcon,
    XIcon,
  },
  props: {
    modelValue: { type: Boolean, required: true },
  },
  emits: ["update:modelValue"],

  data() {
    return {
      keywords: "",
      results: emptyResults(),
      loading: false,
      debounceTimer: null,
      requestToken: 0,
    };
  },

  computed: {
    hasMinKeywords() {
      return this.keywords.trim().length >= 2;
    },
    sections() {
      return [
        {
          key: "organization",
          label: "Organisations",
          items: this.results.organizations.map((o) => ({
            type: "organization",
            id: o.id,
            optionKey: `organization-${o.id}`,
            primary: o.businessName || o.name,
            secondary: o.name,
          })),
        },
        {
          key: "store",
          label: "Stores",
          items: this.results.stores.map((s) => ({
            type: "store",
            id: s.id,
            optionKey: `store-${s.id}`,
            primary: s.salesChannel,
            secondary: s.organization,
          })),
        },
        {
          key: "billing_entity",
          label: "Entités de facturation",
          items: this.results.billingEntities.map((be) => ({
            type: "billing_entity",
            id: be.id,
            optionKey: `billing_entity-${be.id}`,
            primary: be.businessName || be.name,
            secondary: be.organization,
          })),
        },
        {
          key: "plan_sale",
          label: "Contrats",
          items: this.results.planSales.map((ps) => ({
            type: "plan_sale",
            id: ps.friendlyId,
            optionKey: `plan_sale-${ps.id}`,
            primary: ps.friendlyId,
            secondary: joinSecondary([ps.customerName, ps.customerEmail]),
          })),
        },
        {
          key: "claim",
          label: "Sinistres",
          items: this.results.claims.map((c) => ({
            type: "claim",
            id: c.friendlyId,
            optionKey: `claim-${c.id}`,
            primary: c.friendlyId,
            secondary: joinSecondary([c.customerName, c.status]),
          })),
        },
      ];
    },
    visibleSections() {
      return this.sections.filter((s) => s.items.length > 0);
    },
    hasResults() {
      return this.visibleSections.length > 0;
    },
  },

  watch: {
    modelValue(open) {
      if (open) {
        this.reset();
        this.$nextTick(() => this.focusInput());
      } else {
        this.cancelPendingSearch();
      }
    },
  },

  beforeUnmount() {
    this.cancelPendingSearch();
  },

  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },

    reset() {
      this.cancelPendingSearch();
      this.keywords = "";
      this.results = emptyResults();
      this.loading = false;
    },

    cancelPendingSearch() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      this.requestToken += 1;
    },

    focusInput() {
      const input = this.$refs.inputRef;
      const el = input?.$el ?? input;
      if (el && typeof el.focus === "function") el.focus();
    },

    onInput(event) {
      this.keywords = event.target.value;
      this.scheduleSearch();
    },

    scheduleSearch() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      const token = ++this.requestToken;
      const keywords = this.keywords.trim();
      if (!this.hasMinKeywords) {
        this.results = emptyResults();
        this.loading = false;
        return;
      }

      this.loading = true;
      this.debounceTimer = setTimeout(() => this.runSearch(token, keywords), DEBOUNCE_MS);
    },

    async runSearch(token, keywords) {
      try {
        const { data } = await QuickSearchService.search(keywords);
        if (token !== this.requestToken) return;
        const camelData = toCamelCase(data);
        this.results = {
          organizations: camelData?.organizations ?? [],
          stores: camelData?.stores ?? [],
          billingEntities: camelData?.billingEntities ?? [],
          planSales: camelData?.planSales ?? [],
          claims: camelData?.claims ?? [],
        };
      } catch (e) {
        if (token !== this.requestToken) return;
        this.results = emptyResults();
      } finally {
        if (token === this.requestToken) this.loading = false;
      }
    },

    onSelect(item) {
      if (!item) return;
      const path = this.targetPath(item);
      if (path) this.$router.push(path);
      this.close();
    },

    targetPath({ type, id }) {
      switch (type) {
        case "organization":
          return `/organizations/${id}`;
        case "store":
          return `/stores/${id}`;
        case "billing_entity":
          return `/billing-entities/${id}`;
        case "plan_sale":
          return `/plan-sales/${id}`;
        case "claim":
          return `/claims/${id}`;
        default:
          return null;
      }
    },
  },
};
</script>
